import { Radio } from 'antd'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { CloseOutlined, QuestionCircleOutlined } from '@ant-design/icons'

import { useStore } from 'store/useStore'
import { addingEmployeeOptions, AddingEmployeeType, EmployeeKeys } from 'sections/LKSection/modules/EmployeeModule/data'
import { EmployeeRole } from 'sections/LKSection/modules/EmployeeModule/stores/entity/EmployeeRole/EmployeeRole'
import { useChangeUserRole } from 'sections/LKSection/modules/EmployeeModule/utils/useChangeUserRole'
import { getOrderRequirementsStepKey } from 'sections/LKSection/modules/OrderModule/utils'
import { BgBox } from 'sections/LKSection/shared/components/UI/BgBox/BgBox'
import { Margin } from 'components/ui'
import { Button, Checkbox, Divider, Flex, Text, Title, Tooltip } from 'components/uiKit'
import { getLkGeneralKey, getSharedKey } from 'utils/helpers/i18n/getKeys'
import { getEmployeePageRoute } from 'utils/helpers/routes/getRoutes'
import { useResize } from 'utils/hooks/useResize'
import { USER_PERMISSION } from 'shared/types/types'

import { getEmployeePageKeys } from '../../../utils/getKeys'
import { AddEmployeeToSomePvzModal } from './Modal/AddEmployeeToSomePvzModal'

import s from './EmployeeRolesForm.module.scss'

const nowrap = { whiteSpace: 'nowrap' }

const getKey = (key: string) => `${EmployeeKeys.addEmployeePage}.${key}`

type EmployeeRolesFormProps = {
  formType: 'edit' | 'add' | 'recover'
  disabled?: boolean
}

export const EmployeeRolesForm = observer(({ formType, disabled }: EmployeeRolesFormProps) => {
  const { viewEmployeeUiStore: uiStore, viewPvzUiStore } = useStore()

  const navigate = useNavigate()
  const { isSTablet } = useResize()
  const { t } = useTranslation()

  const [addingEmployeeValue, setAddingEmployeeValue] = useState(AddingEmployeeType.only)
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [openELock, setOpenELock] = useState(false)
  const [activateELock, setActivateELock] = useState(false)
  const [loading, setLoading] = useState(false)

  const selectedPvzList = uiStore.employeeRoleList.filter((role) => role.roleWasChangedInModal)
  const selectedPvzId = uiStore.selectedPvz?.id
  const isOwner = viewPvzUiStore.isOwner(selectedPvzId ?? 0)
  const skipButtonLabel = {
    add: t(getEmployeePageKeys('Добавить сотрудника')),
    edit: t(getSharedKey('saveChanges')),
    recover: t(getKey('Восстановить сотрудника')),
  }

  useEffect(() => {
    if (uiStore.selectedUserId) {
      uiStore.getUserPvzRoles(uiStore.selectedUserId)
      uiStore.getUserELockRoles(uiStore.selectedUserId)
    }
  }, [uiStore, uiStore.selectedUserId])

  useEffect(() => {
    return () => uiStore.reset()
  }, [])

  useChangeUserRole(addingEmployeeValue)

  const closeTagHandler = (employeeRole: EmployeeRole) => {
    const changedRoleList = uiStore.employeeRoleList.map((roleItem) => {
      if (roleItem.id === employeeRole?.id) {
        roleItem.setRole(roleItem.cachedRole)
        roleItem.setRoleWasChangedInModal(false)
      }

      return roleItem
    })

    uiStore.employeeRoleStore.putEntities(changedRoleList)
  }

  const changeOpenELockHandler = (value: boolean) => {
    setOpenELock(value)
    uiStore.setSelectedELockRole(value, activateELock)
  }

  const changeActivateELockHandler = (value: boolean) => {
    setActivateELock(value)
    uiStore.setSelectedELockRole(openELock, value)
  }

  const changeRoleHandler = async (isRecover: boolean) => {
    setLoading(true)

    if (isRecover) {
      const res = await uiStore.recoverUser(uiStore.selectedUserId)

      if (!res) {
        return
      }
    }

    await uiStore.updateUserRoles()
    await uiStore.updateUserELockRoles()

    setLoading(false)

    if (selectedPvzId) {
      navigate(getEmployeePageRoute(selectedPvzId))
      uiStore.reset()
    }
  }

  const addEmployeeHandler = () => {
    if (selectedPvzId) {
      navigate(getEmployeePageRoute(selectedPvzId))
      uiStore.reset()
    }
  }

  const recoverEmployeeHandler = async () => {
    if (selectedPvzId) {
      const res = await uiStore.recoverUser(uiStore.selectedUserId)

      if (res) {
        navigate(getEmployeePageRoute(selectedPvzId))
        uiStore.reset()
      }
    }
  }

  return (
    <>
      <BgBox py={32}>
        <Flex vertical style={{ width: '100%' }}>
          <Flex gap={isSTablet ? 16 : 32} align={!isSTablet ? 'center' : undefined} vertical={isSTablet}>
            <Title level={4} style={nowrap}>
              {t(getKey('Роль'))}
            </Title>

            <Radio.Group
              defaultValue={USER_PERMISSION.PERM_EMPLOYEE}
              onChange={(e) => uiStore.setSelectedRole(e.target.value)}
              size="large"
              buttonStyle="solid"
            >
              <Radio.Button value={USER_PERMISSION.PERM_EMPLOYEE}>{t(getKey('Сотрудник'))}</Radio.Button>
              {isOwner && <Radio.Button value={USER_PERMISSION.PERM_OWNER}>{t(getKey('Управляющий'))}</Radio.Button>}
            </Radio.Group>

            <Radio.Group
              value={addingEmployeeValue}
              onChange={(e) => setAddingEmployeeValue(e.target.value)}
              options={addingEmployeeOptions.map((addingEmployeeItem) => ({
                value: addingEmployeeItem.value,
                label: t(getKey(addingEmployeeItem.label)),
              }))}
            />
          </Flex>

          <Margin my={16}>
            <Divider />
          </Margin>
          <Flex gap={isSTablet ? 16 : 32} align={!isSTablet ? 'center' : undefined} vertical={isSTablet}>
            <Flex gap={8}>
              <Title level={4}>{t(getOrderRequirementsStepKey('Электронный замок'))}</Title>
              <Tooltip title={t(getKey('Выбор действий с электронным замком'))}>
                <QuestionCircleOutlined style={{ color: '#00000073' }} />
              </Tooltip>
            </Flex>

            <Checkbox checked={openELock} onChange={(e) => changeOpenELockHandler(e.target.checked)}>
              <Text>{t(getKey('Открытие замка'))}</Text>
            </Checkbox>

            {uiStore.selectedRole === USER_PERMISSION.PERM_OWNER && (
              <Checkbox checked={activateELock} onChange={(e) => changeActivateELockHandler(e.target.checked)}>
                <Text>{t(getKey('Активация замка'))}</Text>
              </Checkbox>
            )}
          </Flex>
          {addingEmployeeValue === AddingEmployeeType.some && (
            <>
              <Margin my={16}>
                <Divider />
              </Margin>

              <Flex
                gap={isSTablet ? 16 : 32}
                align={selectedPvzList.length === 0 && !isSTablet ? 'center' : undefined}
                vertical={isSTablet}
              >
                <Title level={4} style={nowrap}>
                  {t(getLkGeneralKey('ПВЗ'))}
                </Title>

                <Button onClick={() => setIsModalVisible(true)}>{t(getKey('Выбрать из списка'))}</Button>

                <Flex vertical gap={8}>
                  <Text>
                    {t(getKey('Выбрано'))} {selectedPvzList.length}
                  </Text>

                  <Flex wrap gap={8}>
                    {selectedPvzList.map((roleItem) => (
                      <Flex key={roleItem.id} justify="space-between" gap={8} className={s.tag}>
                        <Text style={{ whiteSpace: 'normal' }}>{roleItem.address}</Text>
                        <CloseOutlined style={{ cursor: 'pointer' }} onClick={() => closeTagHandler(roleItem)} />
                      </Flex>
                    ))}
                  </Flex>
                </Flex>
              </Flex>
            </>
          )}
          <Margin my={16}>
            <Divider />
          </Margin>
          <Flex gap={isSTablet ? 8 : 16} vertical={isSTablet}>
            {formType === 'add' && (
              <Button type="default" loading={loading} onClick={addEmployeeHandler}>
                {t(getKey('Пропустить и добавить сотрудника'))}
              </Button>
            )}

            {formType === 'recover' && (
              <Button type="default" loading={loading} onClick={recoverEmployeeHandler}>
                {t(getKey('Пропустить и восстановить сотрудника'))}
              </Button>
            )}

            <Button
              onClick={() => changeRoleHandler(formType === 'recover')}
              loading={loading}
              disabled={(selectedPvzList.length === 0 && addingEmployeeValue === AddingEmployeeType.some) || disabled}
            >
              {skipButtonLabel[formType]}
            </Button>
          </Flex>
        </Flex>
      </BgBox>

      {isModalVisible && <AddEmployeeToSomePvzModal setIsModalOpen={setIsModalVisible} />}
    </>
  )
})
